import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import Checkbox from '@material-ui/core/Checkbox';
import { Tooltip } from '@material-ui/core';
import BrIcons from '@components/BrIcons';
import BrButton from '@components/BrButton';
import BrOutsideAlerter from '@components/BrOutsideAlerter';
import ColorStyles from '@components/ColorStyles';
import './BrDropdownMultiSelectMenuV2.scss';

/* eslint-disable react/no-array-index-key */
const BrDropdownMultiSelectMenuV2 = ({
  className,
  doneBtnColorStyle,
  options,
  showAllSelect,
  selectedOptionList,
  onSelectionDone,
  isLoading,
  onSelectionRemove,
  isSearchRequired,
  showSelectedOptionAsPill,
  selectAllText,
  onOptionSelection,
}) => {
  const [selectedOptions, setSelectedOptions] = useState(selectedOptionList || []);
  const [searchText, setSearchText] = useState('');
  const [groupBy] = useState(true);
  const [shouldShowDropDown, setShouldShowDropDown] = useState(false);

  useEffect(() => {
    setSelectedOptions(selectedOptionList);
  }, [selectedOptionList]);

  const handleOptionSelect = (option) => {
    setShouldShowDropDown(true);
    const index = selectedOptions.findIndex((o) => (o.value === option.value && o.label === option.label));
    if (index === -1) {
      setSelectedOptions([...selectedOptions, option]);
      if (onOptionSelection) {
        onOptionSelection(option);
      }
    } else {
      if (onSelectionRemove) {
        onSelectionRemove(option);
      }
      setSelectedOptions([...selectedOptions.slice(0, index), ...selectedOptions.slice(index + 1)]);
    }
    setSearchText('');
  };

  const handleSelectAll = () => {
    onSelectionDone(selectedOptions);
    if (selectedOptions.length !== options.length) {
      setSelectedOptions(options);
    } else {
      setSelectedOptions([]);
    }
  };

  const handleSearchChange = (event) => {
    if (event.target.value.trim() !== '') {
      setShouldShowDropDown(true);
    }
    setSearchText(event.target.value);
  };

  const handleCloseDropdown = () => {
    setShouldShowDropDown(false);
    onSelectionDone(selectedOptions);
  };

  const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(searchText.toLowerCase()));
  const groupedOptions = groupBy
    ? filteredOptions.reduce((groups, option) => {
      const group = groups.find((g) => g.group === option.group);
      if (group) {
        group.options.push(option);
      } else {
        groups.push({ group: option.group, options: [option] });
      }
      return groups;
    }, [])
    : [{ group: null, options: filteredOptions }];

  const handleClose = () => {
    setShouldShowDropDown(false);
  };

  return (
    <BrOutsideAlerter callbackFunc={handleClose}>
      <div className={`dropdown-multiselect-container ${className}`}>
        <div
          className={cn('selected-options', { 'highlight-dropdown': shouldShowDropDown })}
          onClick={() => {
            setShouldShowDropDown(true);
          }}
        >
          <div className="selected-options-container">
            {selectedOptions.map((option, index) => {
              return showSelectedOptionAsPill ? (
                <span
                  key={index}
                  className="selected-option"
                  onClick={() => {
                    handleOptionSelect(option);
                  }}
                >
                  {option.label}
                  {'    '}
                  &times;
                </span>
              ) : (
                <span className="selected-option-lbl">
                  {' '}
                  { selectedOptions.length === index + 1 ? ` ${option.label}` : ` ${option.label},`}
                  {}
                </span>
              );
            })}

            {isSearchRequired && (
              <input
                type="text"
                placeholder="Select"
                value={searchText}
                className="search-input"
                onChange={handleSearchChange}
              />
            )}
          </div>
          <BrIcons.DownCaret isSubComp />
        </div>
        {shouldShowDropDown && (
          <div className="multi-select-dropdown-options">
            {isLoading ? (
              <BrIcons.Spinner isSubComp />
            ) : (
              <div className="options-container">
                {showAllSelect && (
                  <label className="multi-select-dropdown-option">
                    <Checkbox
                      checked={options.length === selectedOptions.length}
                      onChange={handleSelectAll}
                      value={`All ${selectAllText}`}
                    />
                    All
                    {' '}
                    {selectAllText}
                  </label>
                )}

                {!groupedOptions || groupedOptions.length === 0
                  ? 'No Data Available'
                  : groupedOptions.map((group, index) => (
                    <div key={index} className="multi-select-dropdown-group">
                      {group.group && (
                      <div key={index} className="multi-select-dropdown-group-label">
                        {group.group}
                      </div>
                      )}
                      {group.options.map((option, index) => (
                        /* eslint-disable-next-line jsx-a11y/label-has-associated-control */
                        <label
                          key={index}
                          className="multi-select-dropdown-option"
                        >
                          <Checkbox
                            id={`multi-select-dropdown-option-${index}`}
                            checked={selectedOptions.some(
                              (o) => o.value === option.value && o.label === option.label,
                            )}
                            onChange={() => handleOptionSelect(option)}
                            value={option.value}
                          />
                          <Tooltip
                            arrow
                            placement="top-start"
                            title={(
                              <span
                                style={{
                                  fontSize: '12px',
                                }}
                              >
                                {option.label}
                              </span>
                              )}
                          >
                            <span>{option.label}</span>
                          </Tooltip>
                        </label>
                      ))}
                    </div>
                  ))}
              </div>
            )}

            <div className="btn-container">
              <BrButton key="apply" title="Done" colorStyle={doneBtnColorStyle} onClick={handleCloseDropdown} />
            </div>
          </div>
        )}
      </div>
    </BrOutsideAlerter>
  );
};

BrDropdownMultiSelectMenuV2.propTypes = {
  className: PropTypes.string,
  doneBtnColorStyle: PropTypes.oneOf(Object.values(ColorStyles)), // color style specifically for the "Done" button
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      group: PropTypes.string,
    }),
  ),
  onSelectionDone: PropTypes.func.isRequired,
  onSelectionRemove: PropTypes.func,
  showAllSelect: PropTypes.bool,
  selectedOptionList: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      group: PropTypes.string,
    }),
  ),
  isLoading: PropTypes.bool,
  searchPlaceholderText: PropTypes.string,
  noItemFoundText: PropTypes.string,
};

export default BrDropdownMultiSelectMenuV2;
